import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import ConfirmModal from '../components/ConfirmModal';

const Settings = () => {
  const navigate = useNavigate();
  const { isAdmin, canApprovePayments } = useAuth();

  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    recipient_type: 'TO',
    is_active: true,
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Delete modal
  const [confirmModal, setConfirmModal] = useState({ show: false });

  // Access check
  const hasAccess = isAdmin() || canApprovePayments();

  useEffect(() => {
    if (!hasAccess) {
      navigate('/dashboard');
      return;
    }
    fetchRecipients();
  }, []);

  const fetchRecipients = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/accountant-recipients/');
      setRecipients(Array.isArray(response.data) ? response.data : []);
      setError(null);
    } catch (err) {
      setError('Failed to load accountant recipients');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', recipient_type: 'TO', is_active: true });
    setFormErrors({});
    setEditingId(null);
    setShowForm(false);
  };

  const handleAdd = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEdit = (recipient) => {
    setFormData({
      name: recipient.name,
      email: recipient.email,
      recipient_type: recipient.recipient_type,
      is_active: recipient.is_active,
    });
    setEditingId(recipient.id);
    setFormErrors({});
    setShowForm(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      setError(null);

      if (editingId) {
        await api.put(`/api/accountant-recipients/${editingId}/`, formData);
        setSuccessMessage('Recipient updated successfully');
      } else {
        await api.post('/api/accountant-recipients/', formData);
        setSuccessMessage('Recipient added successfully');
      }

      resetForm();
      await fetchRecipients();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      if (err.response?.data) {
        const serverErrors = err.response.data;
        const mapped = {};
        Object.keys(serverErrors).forEach(key => {
          mapped[key] = Array.isArray(serverErrors[key]) ? serverErrors[key].join(' ') : serverErrors[key];
        });
        setFormErrors(mapped);
      } else {
        setError('Failed to save recipient');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (recipient) => {
    setConfirmModal({
      show: true,
      title: 'Delete Recipient',
      message: `Are you sure you want to delete ${recipient.name} <${recipient.email}>? This cannot be undone.`,
      confirmText: 'Delete',
      confirmStyle: 'btn-danger',
      loading: false,
      onConfirm: async () => {
        try {
          setConfirmModal(prev => ({ ...prev, loading: true }));
          await api.delete(`/api/accountant-recipients/${recipient.id}/`);
          setRecipients(prev => prev.filter(r => r.id !== recipient.id));
          setConfirmModal({ show: false });
          setSuccessMessage('Recipient deleted');
          setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
          setError('Failed to delete recipient');
          setConfirmModal({ show: false });
        }
      },
    });
  };

  const handleToggleActive = async (recipient) => {
    try {
      await api.patch(`/api/accountant-recipients/${recipient.id}/`, {
        is_active: !recipient.is_active,
      });
      setRecipients(prev => prev.map(r =>
        r.id === recipient.id ? { ...r, is_active: !r.is_active } : r
      ));
    } catch (err) {
      setError('Failed to update recipient');
    }
  };

  if (!hasAccess) return null;

  return (
    <div className="settings-container" style={{ padding: '24px', maxWidth: '900px' }}>
      <h1 style={{ fontSize: '24px', fontWeight: '600', color: '#1e293b', marginBottom: '8px' }}>
        Settings
      </h1>
      <p style={{ color: '#64748b', marginBottom: '32px' }}>
        Manage system configuration and preferences.
      </p>

      {/* Success Banner */}
      {successMessage && (
        <div style={{
          backgroundColor: '#ecfdf5',
          border: '1px solid #10b981',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          color: '#065f46',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <strong>✓ {successMessage}</strong>
          <button
            onClick={() => setSuccessMessage(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: '#065f46' }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Error Banner */}
      {error && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #ef4444',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          color: '#991b1b',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <strong>{error}</strong>
          <button
            onClick={() => setError(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: '#991b1b' }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Accountant Recipients Section */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b', margin: 0 }}>
              Accountant Recipients
            </h2>
            <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0' }}>
              Manage email addresses that receive payment schedule files. Only active recipients will receive emails.
            </p>
          </div>
          {!showForm && (
            <button className="btn btn-primary" onClick={handleAdd}>
              Add Recipient
            </button>
          )}
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <div style={{
            padding: '20px 24px',
            backgroundColor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
          }}>
            <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1e293b', margin: '0 0 16px' }}>
              {editingId ? 'Edit Recipient' : 'Add New Recipient'}
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', gap: '12px', alignItems: 'start' }}>
              {/* Name */}
              <div>
                <label style={labelStyle}>Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., John Smith"
                  style={{
                    ...inputStyle,
                    borderColor: formErrors.name ? '#ef4444' : '#d1d5db',
                  }}
                />
                {formErrors.name && <span style={errorStyle}>{formErrors.name}</span>}
              </div>

              {/* Email */}
              <div>
                <label style={labelStyle}>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="e.g., john@company.com"
                  style={{
                    ...inputStyle,
                    borderColor: formErrors.email ? '#ef4444' : '#d1d5db',
                  }}
                />
                {formErrors.email && <span style={errorStyle}>{formErrors.email}</span>}
              </div>

              {/* Type */}
              <div>
                <label style={labelStyle}>Type</label>
                <select
                  value={formData.recipient_type}
                  onChange={(e) => setFormData({ ...formData, recipient_type: e.target.value })}
                  style={inputStyle}
                >
                  <option value="TO">TO</option>
                  <option value="CC">CC</option>
                </select>
              </div>

              {/* Active Toggle */}
              <div>
                <label style={labelStyle}>Active</label>
                <div style={{ paddingTop: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      style={{ width: '16px', height: '16px' }}
                    />
                    <span style={{ fontSize: '14px', color: '#374151' }}>
                      {formData.is_active ? 'Yes' : 'No'}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : editingId ? 'Update' : 'Add'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={resetForm}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Recipients Table */}
        <div style={{ padding: '0' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
              Loading recipients...
            </div>
          ) : recipients.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
              <p style={{ fontSize: '15px', marginBottom: '4px' }}>No accountant recipients configured.</p>
              <p style={{ fontSize: '13px' }}>Add at least one TO recipient to enable payment schedule emails.</p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc' }}>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Status</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {recipients.map((r) => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={tdStyle}>{r.name}</td>
                    <td style={tdStyle}>{r.email}</td>
                    <td style={tdStyle}>
                      <span style={{
                        padding: '2px 10px',
                        borderRadius: '9999px',
                        fontSize: '12px',
                        fontWeight: '500',
                        backgroundColor: r.recipient_type === 'TO' ? '#dbeafe' : '#f3e8ff',
                        color: r.recipient_type === 'TO' ? '#1e40af' : '#6b21a8',
                      }}>
                        {r.recipient_type}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <button
                        onClick={() => handleToggleActive(r)}
                        style={{
                          padding: '2px 10px',
                          borderRadius: '9999px',
                          fontSize: '12px',
                          fontWeight: '500',
                          border: 'none',
                          cursor: 'pointer',
                          backgroundColor: r.is_active ? '#dcfce7' : '#f1f5f9',
                          color: r.is_active ? '#166534' : '#64748b',
                        }}
                        title={r.is_active ? 'Click to deactivate' : 'Click to activate'}
                      >
                        {r.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <button
                        className="btn btn-small btn-secondary"
                        onClick={() => handleEdit(r)}
                        style={{ marginRight: '6px' }}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-small btn-danger"
                        onClick={() => handleDelete(r)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Confirm Modal */}
      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        confirmStyle={confirmModal.confirmStyle}
        loading={confirmModal.loading}
        cancelText="Cancel"
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ show: false })}
      />
    </div>
  );
};

// Shared styles
const labelStyle = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '600',
  color: '#374151',
  marginBottom: '4px',
};

const inputStyle = {
  width: '100%',
  padding: '8px 12px',
  fontSize: '14px',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  outline: 'none',
  boxSizing: 'border-box',
};

const errorStyle = {
  display: 'block',
  fontSize: '12px',
  color: '#ef4444',
  marginTop: '2px',
};

const thStyle = {
  padding: '10px 24px',
  textAlign: 'left',
  fontSize: '12px',
  fontWeight: '600',
  color: '#64748b',
  textTransform: 'uppercase',
  borderBottom: '1px solid #e2e8f0',
};

const tdStyle = {
  padding: '12px 24px',
  fontSize: '14px',
  color: '#1e293b',
};

export default Settings;